import { useEffect, useMemo, useState } from 'react';
import { useToast } from '@/components/common/toast';
import { Select2 } from '@/components/forms/Select2';
import { FilterBar } from '@/components/common/FilterBar';
import { Spinner } from '@/components/common/Spinner';
import { ApiError } from '@/api/client/abpError';
import {
  IF1_EDITABLE_PROPS,
  IF1_ROWS,
  if1Api,
  if1Calc,
  if1Fmt,
  if1Parse,
  rowCode,
  type ProfileIF1UpdateDto,
} from './if1';

interface Block {
  id: string;
  concurrencyStamp: string;
  year: number;
  quarter: number;
  values: Record<string, number>;
}
const keyOf = (q: number, y: number) => `Quarter-${q}-${y}`;

export function If1Tab() {
  const toast = useToast();
  const now = useMemo(() => new Date(), []);
  const curQ = Math.max(1, Math.min(4, Math.floor(now.getMonth() / 3) + 1));

  const [blocks, setBlocks] = useState<Record<string, Block>>({});
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [period, setPeriod] = useState(String(curQ));
  const [year, setYear] = useState(now.getFullYear());

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Best-effort: seeding the quarters shouldn't block showing existing data if it
        // fails (the current quarter is usually already present).
        try {
          await if1Api.ensureQuarters({ currentYear: now.getFullYear(), currentMonth: now.getMonth() + 1 });
        } catch { /* ignore — fall through to loading whatever exists */ }
        const list = await if1Api.getList();
        if (!active) return;
        const map: Record<string, Block> = {};
        for (const d of list) {
          const values: Record<string, number> = {};
          for (const p of IF1_EDITABLE_PROPS) values[p] = Number(d[p] ?? 0);
          map[keyOf(d.quarter, d.year)] = { id: d.id, concurrencyStamp: d.concurrencyStamp, year: d.year, quarter: d.quarter, values };
        }
        setBlocks(map);
        const ck = keyOf(curQ, now.getFullYear());
        setActiveKey(map[ck] ? ck : Object.keys(map)[0] ?? null);
      } catch (e) {
        if (active) toast.error(e instanceof ApiError ? e.message : 'Failed to load IF 01.00 data.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const block = activeKey ? blocks[activeKey] : null;
  const computed = useMemo(() => (block ? if1Calc(block.values) : {}), [block]);

  const setVal = (prop: string, n: number) => {
    if (!activeKey) return;
    setBlocks((b) => ({ ...b, [activeKey]: { ...b[activeKey], values: { ...b[activeKey].values, [prop]: n } } }));
  };

  const onFetch = () => {
    const k = keyOf(Number(period), year);
    if (blocks[k]) setActiveKey(k);
    else toast.error('No data available for the chosen reference date');
  };
  const onCancel = () => {
    setPeriod('4');
    const k = keyOf(4, year);
    if (blocks[k]) setActiveKey(k);
    else {
      const first = Object.keys(blocks)[0];
      if (first) setActiveKey(first);
      toast.error('No data available for Q4 of the selected year');
    }
  };
  const onUpdate = async () => {
    const updates: ProfileIF1UpdateDto[] = Object.values(blocks).map((b) => {
      const dto: Record<string, number | string> = { id: b.id, concurrencyStamp: b.concurrencyStamp };
      for (const p of IF1_EDITABLE_PROPS) dto[p] = b.values[p] ?? 0;
      return dto as unknown as ProfileIF1UpdateDto;
    });
    if (!updates.length) { toast.info('Nothing to update.'); return; }
    setSaving(true);
    try {
      await if1Api.updateMany(updates);
      toast.success('IF1 updated successfully');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to update IF1.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      <h6 className="fw-semibold mb-2">IF 01.00 - OWN FUNDS COMPOSITION (IF1):</h6>
      <FilterBar
        fields={[
          {
            node: (
              <div>
                <label className="form-label">Period</label>
                <Select2 searchable={false} allowClear={false} value={period} options={[{ value: '1', label: 'Q1' }, { value: '2', label: 'Q2' }, { value: '3', label: 'Q3' }, { value: '4', label: 'Q4' }]} onChange={(v) => setPeriod(v || '4')} />
              </div>
            ),
            width: 120,
          },
          {
            node: (
              <div>
                <label className="form-label">Year</label>
                <input type="number" className="form-control" min={1900} max={now.getFullYear()} value={year} onChange={(e) => setYear(Number(e.target.value) || now.getFullYear())} />
              </div>
            ),
            width: 120,
          },
        ]}
        actions={
          <>
            <button type="button" className="btn btn-primary btn-sm" onClick={onFetch}><i className="fa fa-search me-1" />Fetch</button>
            <button type="button" className="btn btn-link btn-sm" onClick={onCancel}>Cancel</button>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={onUpdate} disabled={saving}>
              {saving ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-save me-1" />}Update
            </button>
          </>
        }
      />


      {!block ? (
        <p className="text-muted">No data available for the chosen reference date.</p>
      ) : (
        <div className="table-responsive">
          <table className="table dataTable table-striped table-hover table-sm mb-0 w-100">
            <thead>
              <tr>
                <th style={{ width: 70 }}>Rows</th>
                <th>Item</th>
                <th className="text-end" style={{ width: 240 }}>0010</th>
              </tr>
            </thead>
            <tbody>
              {IF1_ROWS.map((r, i) => {
                const isComputed = !r.editable;
                return (
                  <tr key={r.prop} className={isComputed ? 'fw-semibold' : undefined}>
                    <td className="text-muted">{rowCode(i)}</td>
                    <td style={{ whiteSpace: 'normal' }}>{r.label}</td>
                    <td className="text-end">
                      {isComputed ? (
                        <input className="form-control form-control-sm text-end" disabled value={if1Fmt(computed[r.prop] ?? 0)} readOnly />
                      ) : (
                        <input
                          className="form-control form-control-sm text-end"
                          key={`${activeKey}:${r.prop}`}
                          defaultValue={if1Fmt(block.values[r.prop] ?? 0)}
                          onBlur={(e) => {
                            let n = if1Parse(e.target.value);
                            if (r.neg && n > 0) { n = 0; toast.info('This field accepts negative numbers only.'); }
                            e.target.value = if1Fmt(n);
                            setVal(r.prop, n);
                          }}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
